import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  useProviderAPIKeyMutation,
  useSetApiKeyMutation
} from "../../app/services/chatbot-rtk.services";
import { selectClient } from "../../features/dashboard.slice";
import { useAppSelector } from "../../app/hook";

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  openAiKey: Yup.string()
    .required("API key is required.")
    .matches(/^sk-/, "Invalid API key format. Please check the key."),
  anthropicKey: Yup.string()
    .required("API key is required.")
    .matches(/^sk-/, "Invalid API key format. Please check the key.")
});

interface FormValues {
  openAiKey: string;
  anthropicKey: string;
}

const ApiKeyConfiguration: React.FC = () => {
  const { t } = useTranslation();
  const [providerAPIKey, providerAPIKeyStatus] = useProviderAPIKeyMutation();
  const [setApiKey, setApiKeyStatus] = useSetApiKeyMutation();
  const client = useAppSelector(selectClient);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema)
  });

  const [showModal, setShowModal] = useState(false);

  const onSubmit: SubmitHandler<FormValues> = () => {
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);
  const openAiKey = watch("openAiKey");
  const anthropicKey = watch("anthropicKey");
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (client?.config?.apiKeys) {
      setValue("openAiKey", client?.config?.apiKeys?.openai ?? "");
      setValue("anthropicKey", client?.config?.apiKeys?.anthropic ?? "");
    }
  }, [client?.config?.apiKeys]);

  const onSubmitAI = () => {
    const openaiPromise = providerAPIKey({
      apiKey: openAiKey,
      provider: "openai"
    })
      .unwrap()
      .catch((err) => {
        throw err;
      });

    const anthropicPromise = providerAPIKey({
      apiKey: anthropicKey,
      provider: "anthropic"
    })
      .unwrap()
      .catch((err) => {
        throw err; // To ensure the Promise.all fails if this fails
      });

    Promise.all([openaiPromise, anthropicPromise])
      .then(() => {
        return setApiKey({
          anthropic: anthropicKey,
          openai: openAiKey
        }).unwrap();
      })
      .then((res) => {
        toast.success(t("BusinessPartner/Payout/successEdit"));
        setShowModal(false);
      })
      .catch((err) => {
        toast.error(err?.data?.message || err?.message);
      });
  };

  useEffect(() => {
    if (showModal == false) {
      setInputValue("");
    }
  }, [showModal]);

  const checkDisabledButton = () => {
    if (
      inputValue !== "CONFIRM" ||
      providerAPIKeyStatus.isLoading ||
      setApiKeyStatus.isLoading ||
      client?.config?.apiKeys?.openai ||
      client?.config?.apiKeys?.anthropic
    ) {
      return true;
    }

    return false;
  };

  const checkDisabledMigrationButton = () => {
    if (
      !openAiKey ||
      !anthropicKey ||
      client?.config?.apiKeys?.openai ||
      client?.config?.apiKeys?.anthropic
    ) {
      return true;
    }

    return false;
  };

  return (
    <div className="tw-p-5 tw-bg-white tw-rounded-lg tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
      <div className="tw-w-full md:tw-flex-row tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
        <div className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
          {t("DashboardSettingWidgetDefaultDomain/key2")}
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="tw-flex tw-flex-col tw-gap-10 tw-w-full"
      >
        <div className="tw-flex tw-flex-col tw-justify-between tw-gap-5">
          <div className="tw-w-full">
            <Form.Group>
              <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                OpenAi Api key
              </Form.Label>
              <Form.Control
                {...register("openAiKey")}
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                placeholder={t(
                  "DashboardSettingWidgetDefaultDomain/api-placeholder"
                )}
              />
              {errors.openAiKey && (
                <Form.Control.Feedback
                  type="invalid"
                  className="d-block font-poppins"
                >
                  {errors.openAiKey.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </div>

          <div className="tw-w-full">
            <Form.Group>
              <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                Anthropic Api key
              </Form.Label>
              <Form.Control
                {...register("anthropicKey")}
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                placeholder={t(
                  "DashboardSettingWidgetDefaultDomain/api-placeholder"
                )}
              />

              {errors.anthropicKey && (
                <Form.Control.Feedback
                  type="invalid"
                  className="d-block font-poppins"
                >
                  {errors.anthropicKey.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </div>
        </div>

        <div className="tw-flex tw-flex-row tw-justify-end">
          <button
            disabled={checkDisabledMigrationButton()}
            type="submit"
            className={`tw-px-[17px] tw-py-[9px] ${
              checkDisabledMigrationButton()
                ? "tw-bg-gray-400"
                : "tw-bg-primary"
            } tw-border-0 tw-rounded-[99px] tw-shadow tw-justify-center tw-items-center tw-inline-flex tw-text-white tw-text-sm font-normal tw-font-['Lexend'] tw-leading-tight`}
          >
            {(client?.config?.apiKeys?.openai ||
              client?.config?.apiKeys?.anthropic) && (
              <>{t("APIKeyConfirmation/btn-02")}</>
            )}
            {!client?.config?.apiKeys?.openai &&
              !client?.config?.apiKeys?.anthropic && (
                <>{t("DashboardSettingWidgetDefaultDomain/btn-04")}</>
              )}
          </button>
        </div>
      </form>

      <Modal
        centered
        show={showModal}
        onHide={closeModal}
        dialogClassName="tw-max-w-[400px]"
      >
        <Modal.Body className="tw-py-5 tw-px-6">
          <div className="tw-flex tw-flex-col tw-w-full tw-items-center tw-justify-center tw-gap-y-3">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="40" height="40" rx="20" fill="#FEF3C7" />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18.2571 13.0996C19.0217 11.7403 20.9788 11.7403 21.7434 13.0996L27.3237 23.0201C28.0736 24.3533 27.1102 26.0006 25.5805 26.0006H14.4199C12.8903 26.0006 11.9268 24.3533 12.6768 23.0201L18.2571 13.0996ZM21.0001 23.0007C21.0001 23.553 20.5524 24.0007 20.0001 24.0007C19.4478 24.0007 19.0001 23.553 19.0001 23.0007C19.0001 22.4484 19.4478 22.0007 20.0001 22.0007C20.5524 22.0007 21.0001 22.4484 21.0001 23.0007ZM20.0001 15.0007C19.4478 15.0007 19.0001 15.4484 19.0001 16.0007V19.0007C19.0001 19.553 19.4478 20.0007 20.0001 20.0007C20.5524 20.0007 21.0001 19.553 21.0001 19.0007V16.0007C21.0001 15.4484 20.5524 15.0007 20.0001 15.0007Z"
                fill="#F59E0B"
              />
            </svg>
            <div className="tw-self-stretch tw-flex-col tw-justify-center tw-items-center tw-gap-2 tw-flex tw-my-[10px]">
              <div className="tw-self-stretch tw-text-center tw-text-gray-700 tw-text-xl tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                {t("APIKeyConfirmation/l-01")}
              </div>
              <div className=" tw-text-center">
                <span className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t("APIKeyConfirmation/l-02")}
                  <br />
                  {t("APIKeyConfirmation/l-03")}
                  <br />
                  <br /> {t("APIKeyConfirmation/l-04")} <br />
                  {t("APIKeyConfirmation/l-06")}{" "}
                </span>
                <br />
                <span className="tw-text-gray-700 tw-text-sm tw-font-extrabold tw-font-['Lexend'] tw-leading-tight">
                  {t("APIKeyConfirmation/l-05")}
                </span>
                <span className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  .
                </span>
              </div>
            </div>
            <div className="tw-flex tw-flex-col tw-w-full tw-gap-y-4 tw-my-8">
              <Form.Group>
                <Form.Control
                  placeholder="CONFIRM"
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                  }}
                  className="tw-font-['Lexend']"
                />
              </Form.Group>
              <Button
                disabled={checkDisabledButton()}
                onClick={() => {
                  onSubmitAI();
                }}
                className="tw-w-full tw-cursor-pointer tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-grow tw-shrink tw-basis-0 tw-h-[38px] tw-px-[17px] tw-py-[9px] tw-bg-primary hover:tw-bg-blue-700 disabled:tw-bg-blue-700 tw-rounded-[99px] tw-justify-center tw-items-center tw-flex"
              >
                {providerAPIKeyStatus.isLoading || setApiKeyStatus.isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <> {t("APIKeyConfirmation/btn-01")}</>
                )}
              </Button>
              <Button
                onClick={closeModal}
                className="tw-grow tw-shrink tw-basis-0 tw-h-[42px] tw-px-[17px] tw-py-[9px] tw-bg-white hover:tw-bg-white hover:tw-text-gray-800 tw-border tw-border-solid tw-border-gray-300 hover:tw-border-gray-300  tw-rounded-[99px] tw-justify-center tw-items-center tw-flex tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              >
                {t("DeletAccount/btn-01")}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ApiKeyConfiguration;
